import {isEqual} from 'lodash';

import {ReportFilterOptionsEnum} from '../../../../analytics/helpers/analyticsReportHelpers';
import {getAnalyticsTrackingData} from '../../../../analytics/helpers/tracking/analyticsSelectors';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {ActionBase, ActionWithPayload} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsDetailsConversation,
  AnalyticsDetailsConversations,
  AnalyticsDetailsConversationsApi,
  ReportFilterOptionsWithContributingSegmentOptions,
} from '../../../../models/api/analyticsMetricDetailsModel';
import {AnalyticsMetricsKeys} from '../../../../models/api/analyticsMetricsModel';
import {postApiAsync} from '../../../../services/http/http';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {getCompany} from '../../../data/company/companySelectors';
import {buildAnalyticsQueryFromState, makeAnalyticsApiPath} from '../analyticsPanelHelpers';
import {
  getAnalyticsReportType,
  getAnalyticsSelectedNamespace,
} from '../parameters/analyticsPanelParametersSelectors';
import {
  getAnalyticsDetailsConversationsPageNumber,
  getAnalyticsDetailsSelectedMetricWithOptions,
  getIsConversationDetailsSidePanelOpen,
  getIsSidePanelOpen,
} from './analyticsPanelDetailsSelectors';

/*
 * Const.
 */

export const SIZE_CONVERSATIONS_PAGE = 10;

/*
 * Type enum.
 */

export enum AnalyticsPanelDetailsActionsEnum {
  SET_METRIC = '@@panels/analytics/details/SET_METRIC',
  FETCH_METRICS_DETAILS = '@@panels/analytics/details/FETCH_METRICS_DETAILS',
  FETCH_METRICS_DETAILS_COMPLETED = '@@panels/analytics/details/FETCH_METRICS_DETAILS_COMPLETED',
  FETCH_METRICS_DETAILS_FAILED = '@@panels/analytics/details/FETCH_METRICS_DETAILS_FAILED',
  SET_CONVERSATIONS_PAGE_NUMBER = '@@panels/analytics/details/SET_CONVERSATIONS_PAGE_NUMBER',
  SET_IS_SIDE_PANEL_OPEN = '@@panels/analytics/details/SET_IS_SIDE_PANEL_OPEN',
  SET_IS_CONVERSATION_DETAILS_SIDE_PANEL_OPEN = '@@panels/analytics/details/SET_IS_CONVERSATION_DETAILS_SIDE_PANEL_OPEN',
  SET_SELECTED_CONVERSATION = '@@panels/analytics/details/SET_SELECTED_CONVERSATION',
}

interface SetMetricPayload {
  metric: AnalyticsMetricsKeys;
  options?: ReportFilterOptionsWithContributingSegmentOptions;
  customLabel?: string;
}

/*
 * Types.
 */

interface SetMetricAction extends ActionWithPayload<SetMetricPayload> {
  type: AnalyticsPanelDetailsActionsEnum.SET_METRIC;
}

interface FetchMetricDetailsAction extends ActionBase {
  type: AnalyticsPanelDetailsActionsEnum.FETCH_METRICS_DETAILS;
}

interface FetchMetricDetailsCompletedAction extends ActionWithPayload<AnalyticsDetailsConversations> {
  type: AnalyticsPanelDetailsActionsEnum.FETCH_METRICS_DETAILS_COMPLETED;
}

interface FetchMetricDetailsFailedAction extends ActionWithPayload<string> {
  type: AnalyticsPanelDetailsActionsEnum.FETCH_METRICS_DETAILS_FAILED;
}

export interface SetConversationsPageNumberAction extends ActionWithPayload<number> {
  type: AnalyticsPanelDetailsActionsEnum.SET_CONVERSATIONS_PAGE_NUMBER;
}
interface SetIsSidePanelOpenAction extends ActionWithPayload<boolean> {
  type: AnalyticsPanelDetailsActionsEnum.SET_IS_SIDE_PANEL_OPEN;
}

interface SetIsConversationDetailsSidePanelOpenAction extends ActionWithPayload<boolean> {
  type: AnalyticsPanelDetailsActionsEnum.SET_IS_CONVERSATION_DETAILS_SIDE_PANEL_OPEN;
}
interface SetSelectedConversationAction extends ActionWithPayload<AnalyticsDetailsConversation | undefined> {
  type: AnalyticsPanelDetailsActionsEnum.SET_SELECTED_CONVERSATION;
}

/*
 * Action creators.
 */

export function setIsSidePanelOpenAction(isSidePanelOpen: boolean): ThunkAction {
  return (dispatch) => {
    dispatch({
      type: AnalyticsPanelDetailsActionsEnum.SET_IS_SIDE_PANEL_OPEN,
      payload: isSidePanelOpen,
    });
  };
}

export function setIsConversationDetailsSidePanelOpenAction(
  isConversationDetailsSidePanelOpen: boolean,
): ThunkAction {
  return (dispatch) => {
    dispatch({
      type: AnalyticsPanelDetailsActionsEnum.SET_IS_CONVERSATION_DETAILS_SIDE_PANEL_OPEN,
      payload: isConversationDetailsSidePanelOpen,
    });
  };
}

export function setSelectedConversationAction(
  selectedConversation: AnalyticsDetailsConversation | undefined,
): ThunkAction {
  return (dispatch) => {
    dispatch({
      type: AnalyticsPanelDetailsActionsEnum.SET_SELECTED_CONVERSATION,
      payload: selectedConversation,
    });

    dispatch(setIsConversationDetailsSidePanelOpenAction(true));
  };
}

export function setMetricDetailsAction(
  metric: AnalyticsMetricsKeys,
  options?: ReportFilterOptionsWithContributingSegmentOptions,
  customLabel?: string, // Right now we can't dynamically set the submetric labels for the Table component because they're coming from the BE, so passing them manually in the payload.
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const trackingData = getAnalyticsTrackingData(state);
    const isSidePanelOpened = getIsSidePanelOpen(state);
    const isConversationDetailsSidePanelOpen = getIsConversationDetailsSidePanelOpen(state);
    const {selectedMetric, options: stateOptions} = getAnalyticsDetailsSelectedMetricWithOptions(state);

    if (!isSidePanelOpened) {
      dispatch(setIsSidePanelOpenAction(true));
    }

    if (isConversationDetailsSidePanelOpen) {
      dispatch(setIsConversationDetailsSidePanelOpenAction(false));
    }

    if (selectedMetric === metric && isEqual(options, stateOptions)) {
      // Metric & options didn't change, we return.
      return undefined;
    }

    // Track selecting metric event.
    trackInterfaceEvent(InterfaceEventsEnum.ANALYTICS_DETAILS_DRAWER_OPEN, {
      ...trackingData,
      metric_type: metric,
      contributing_segments_options: options,
    });

    // Will open the panel.
    dispatch<SetMetricAction>({
      type: AnalyticsPanelDetailsActionsEnum.SET_METRIC,
      payload: {metric, options, customLabel},
    });

    // Start fetching the metric details data.
    return dispatch(fetchMetricDetailsAction());
  };
}

export function fetchMetricDetailsAction(
  cancelToken?: CancelToken,
): ThunkAsyncAction<FetchMetricDetailsCompletedAction | FetchMetricDetailsFailedAction | undefined> {
  return async (dispatch, getState) => {
    // State.
    const state = getState();
    const company = getCompany(state);
    const namespace = getAnalyticsSelectedNamespace(state);
    const reportType = getAnalyticsReportType(state);
    const {selectedMetric, options} = getAnalyticsDetailsSelectedMetricWithOptions(state);
    const conversationsPageNumber = getAnalyticsDetailsConversationsPageNumber(state);

    if (!selectedMetric) {
      // No metric is selected. We return.
      return undefined;
    }

    dispatch<FetchMetricDetailsAction>({
      type: AnalyticsPanelDetailsActionsEnum.FETCH_METRICS_DETAILS,
    });

    // Build the query.
    const query = buildAnalyticsQueryFromState(
      state,
      {metric: selectedMetric},
      options ?? {
        [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: true,
      },
    );
    const path = getAnalyticsMetricDetailsApiPath(
      company.url,
      namespace,
      reportType,
      (conversationsPageNumber - 1) * SIZE_CONVERSATIONS_PAGE, // Pages start at 1 in the code, hence the -1.
    );

    try {
      // Initiate a fetch from the network.
      const metricDetailsPromise = postApiAsync(AnalyticsDetailsConversationsApi, path, query, {cancelToken});

      const metricDetails = await metricDetailsPromise;

      return dispatch({
        type: AnalyticsPanelDetailsActionsEnum.FETCH_METRICS_DETAILS_COMPLETED,
        payload: metricDetails,
      });
    } catch (err) {
      // @TODO handle errors in UI.
      return dispatch<FetchMetricDetailsFailedAction>({
        type: AnalyticsPanelDetailsActionsEnum.FETCH_METRICS_DETAILS_FAILED,
        payload: 'Something wrong happened while retrieving metrics details!',
      });
    }
  };
}

export function analyticsChangeConversationsPageAction(pageNumber: number): ThunkAction {
  return (dispatch) => {
    // Set the new page number.
    dispatch<SetConversationsPageNumberAction>({
      type: AnalyticsPanelDetailsActionsEnum.SET_CONVERSATIONS_PAGE_NUMBER,
      payload: pageNumber,
    });

    // Fetch new page.
    return dispatch(fetchMetricDetailsAction());
  };
}

/*
 * Union type.
 */

export type AnalyticsPanelDetailsAction =
  | SetMetricAction
  | FetchMetricDetailsAction
  | FetchMetricDetailsCompletedAction
  | FetchMetricDetailsFailedAction
  | SetConversationsPageNumberAction
  | SetIsSidePanelOpenAction
  | SetIsConversationDetailsSidePanelOpenAction
  | SetSelectedConversationAction;

/*
 * Helpers.
 */

const getAnalyticsMetricDetailsApiPath = (
  companyUrl: string,
  selectedNamespace: string,
  report: string,
  offset = 0,
) =>
  `
${makeAnalyticsApiPath(
  companyUrl,
  selectedNamespace,
)}/metrics/${report}/details?limit=${SIZE_CONVERSATIONS_PAGE}&offset=${offset}`;
